import { useState, useEffect } from 'react';

const GOOGLE_ANAL_ID = process.env.REACT_APP_GOOGLE_ANALYTICS_ID;

/**
 * Custom hook to manage cookie consent preferences
 * @param {string} pathname - Current route pathname
 * @param {boolean} isOAuthRedirect - Whether current page is an OAuth redirect
 * @returns {Object} - Cookie consent state and handlers
 */
const useCookieConsent = (pathname, isOAuthRedirect) => {
  const [hasGivenConsent, setHasGivenConsent] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always needed
    analytics: false, // Default unchecked
    preferences: false // Default unchecked
  });

  // Check consent status initially
  useEffect(() => {
    const savedPreferences = localStorage.getItem('cookiePreferences');
    const hasConsent = document.cookie.includes('userConsent=true');
    setHasGivenConsent(!!savedPreferences || hasConsent);
  }, []);

  // Check consent status on navigation
  useEffect(() => {
    // Skip checking on privacy policy or terms pages
    if (pathname.includes('/privacy') || 
        pathname.includes('/terms') || 
        pathname.includes('/disclaimer')) {
      return;
    }
    
    const savedPreferences = localStorage.getItem('cookiePreferences');
    const hasConsent = document.cookie.includes('userConsent=true');
    setHasGivenConsent(!!savedPreferences || hasConsent);
  }, [pathname]);

  // Load analytics if consent given
  useEffect(() => {
    const savedPreferences = localStorage.getItem('cookiePreferences');
    if (savedPreferences) {
      const preferences = JSON.parse(savedPreferences);
      setCookiePreferences(preferences);
      
      // Load Google Analytics if analytics consent is given
      if (preferences.analytics && GOOGLE_ANAL_ID && !window.gtag) {
        const gtagScript = document.createElement('script');
        gtagScript.async = true;
        gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANAL_ID}`;
        document.head.appendChild(gtagScript);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        window.gtag('config', GOOGLE_ANAL_ID);
      }
    }
  }, []);

  // Handle consent acceptance
  const handleAcceptConsent = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    setHasGivenConsent(true);
    
    // Load Google Analytics if analytics consent is given
    if (cookiePreferences.analytics && GOOGLE_ANAL_ID && !window.gtag) {
      const gtagScript = document.createElement('script');
      gtagScript.async = true;
      gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANAL_ID}`;
      document.head.appendChild(gtagScript);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', GOOGLE_ANAL_ID);
    }
  };

  // Handle consent decline
  const handleDeclineConsent = () => {
    const domain = window.location.hostname === 'localhost' ? 'localhost' : 'limkc.com';
    
    // Clear Google Analytics cookies
    document.cookie = '_ga=; Max-Age=-1; path=/; domain=' + domain;
    document.cookie = '_gid=; Max-Age=-1; path=/; domain=' + domain;
    
    // Clear social login cookies
    document.cookie = 'fblo_=; Max-Age=-1; path=/; domain=' + domain;
    document.cookie = 'li_at=; Max-Age=-1; path=/; domain=' + domain;
    
    // Set preferences to only necessary cookies
    const declinedPreferences = { necessary: true, analytics: false, preferences: false };
    localStorage.setItem('cookiePreferences', JSON.stringify(declinedPreferences));
    setCookiePreferences(declinedPreferences);
    setHasGivenConsent(true); // Still mark as given consent since they made a choice
  };

  // Determine if banner should be shown
  const shouldShowBanner = !hasGivenConsent && 
                          !isOAuthRedirect && 
                          !pathname.includes('/privacy') && 
                          !pathname.includes('/terms') && 
                          !pathname.includes('/disclaimer');

  return {
    hasGivenConsent,
    cookiePreferences,
    setCookiePreferences,
    handleAcceptConsent,
    handleDeclineConsent,
    shouldShowBanner
  };
};

export default useCookieConsent;